/**
 * @fileoverview Unified logging utility for the Embedded SDK.
 */

import { SDK_VERSION } from "./constants";

/**
 * Logger configuration.
 */
interface LoggerConfig {
  /** Whether to show the version badge */
  showVersion?: boolean;
  /** Whether debug mode is enabled */
  debug?: boolean;
}

/**
 * Log level types supported by the logger.
 */
type LogType = "log" | "warn" | "error" | "info" | "debug";

/**
 * Global logger configuration.
 */
let loggerConfig: LoggerConfig = {
  showVersion: true,
  debug: false,
};

/**
 * Set the logger configuration.
 *
 * @param config - Logger configuration options
 */
export function setLoggerConfig(config: LoggerConfig): void {
  loggerConfig = { ...loggerConfig, ...config };
}

/**
 * Get the current logger configuration.
 */
export function getLoggerConfig(): Readonly<LoggerConfig> {
  return { ...loggerConfig };
}

/**
 * Create styled badge for console output.
 *
 * @param text - Badge text
 * @returns Formatted badge string
 */
function createBadge(text: string): string {
  return `%c${text}`;
}

/**
 * Get CSS styles for a badge.
 *
 * @param backgroundColor - CSS color for background
 * @param textColor - CSS color for text
 * @returns CSS style string
 */
function getBadgeStyle(backgroundColor: string, textColor = "#fff"): string {
  return `background-color: ${backgroundColor}; color: ${textColor}; padding: 2px 6px; border-radius: 3px; font-weight: 500; font-size: 11px;`;
}

/**
 * Unified logging function that supports all console log types.
 *
 * @param type - Log type (log, warn, error, info, debug)
 * @param args - Arguments to log
 */
export function log(type: LogType, ...args: unknown[]): void {
  // Skip debug logs if debug mode is disabled
  if (type === "debug" && !loggerConfig.debug) {
    return;
  }

  const badges: string[] = [];
  const styles: string[] = [];

  // Add EmbeddedSDK badge (green)
  badges.push(createBadge("EmbeddedSDK"));
  styles.push(getBadgeStyle("#10b981", "#fff"));

  // Add version badge (gray) if enabled
  if (loggerConfig.showVersion) {
    badges.push(createBadge(`v${SDK_VERSION}`));
    styles.push(getBadgeStyle("#6b7280", "#fff"));
  }

  // Combine badges without space
  const badgeString = badges.join("").trim();

  // Get the appropriate console method
  const consoleMethod = console[type] || console.log;

  // Call console method with badges and styles
  consoleMethod(badgeString, ...styles, ...args);
}

/**
 * Convenience methods for each log type.
 */
export const logger = {
  log: (...args: unknown[]) => {
    log("log", ...args);
  },
  warn: (...args: unknown[]) => {
    log("warn", ...args);
  },
  error: (...args: unknown[]) => {
    log("error", ...args);
  },
  info: (...args: unknown[]) => {
    log("info", ...args);
  },
  debug: (...args: unknown[]) => {
    log("debug", ...args);
  },
};
